import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  createBranch,
  deleteBranch,
  fetchBranches,
  fetchTenant,
  setBranchEnabled,
  updateBranch,
  updateTenant,
} from '../api'
import type { SaveBranchBody, UpdateTenantBody } from '../types'

export const businessKey = ['business'] as const
export const tenantKey = [...businessKey, 'tenant'] as const
/**
 * Exported because half the Back Office reads it: hours, tables, orders and the
 * discount form all pick a branch, and they should share one cache entry rather
 * than each fetching the list again.
 */
export const branchesKey = [...businessKey, 'branches'] as const

export function useTenant() {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: tenantKey,
    queryFn: fetchTenant,
  })

  return {
    tenant: data ?? null,
    isLoading: isPending,
    error,
    retry: () => void refetch(),
  }
}

/**
 * The tenant's branches.
 *
 * Long-lived on purpose: a branch list changes when a merchant opens an outlet,
 * not while they work, and re-fetching it on every screen that shows a branch
 * picker would be a request per navigation for a list that has not moved.
 */
export function useBranches() {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: branchesKey,
    queryFn: fetchBranches,
    staleTime: 5 * 60_000,
  })

  return {
    branches: data ?? [],
    isLoading: isPending,
    error,
    retry: () => void refetch(),
  }
}

export function useTenantActions() {
  const queryClient = useQueryClient()

  const save = useMutation({
    mutationFn: (body: UpdateTenantBody) => updateTenant(body),
    // The branch list embeds its tenant, so a rename shows up there too.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: businessKey }),
  })

  return { save }
}

export function useBranchActions() {
  const queryClient = useQueryClient()

  function invalidate() {
    return queryClient.invalidateQueries({ queryKey: branchesKey })
  }

  const save = useMutation({
    mutationFn: ({ id, body }: { id: string | null; body: SaveBranchBody }) =>
      id ? updateBranch(id, body) : createBranch(body),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => deleteBranch(id),
    onSuccess: invalidate,
  })

  const toggleEnabled = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      setBranchEnabled(id, enabled),
    onSuccess: invalidate,
  })

  return { save, remove, toggleEnabled }
}
